// © Microsoft Corporation. All rights reserved.

import { baseMeUrl, baseSignInUrl, baseSignOutUrl, meControlComponentContainerId } from "./MeControlComponentConstants";

import { SSOSignInProperties } from "@msnews/auth";
import { String } from "typescript-string-operations";
import { appEnvironment } from "@msnews/core";

/**
 * The MeControlComponent's configuration options.
 */
export interface MeControlComponentOptions {
    /**
     * The API generation of the Me Control to display, should always be "GEN2" unless we have a use case for another generation
     */
    apiGeneration: string,

    /**
     * The id of the html div element that will contain the Me Control
     */
    containerId: string,

    /**
     * Config options typically provided by the auth provider
     */
    authProviderConfig: {
        /**
         * authorization provider type (see MeControl specs at https://mecontroltestpartner.azurewebsites.net/PartnerDocs/articles/guides/generalConfiguration.html)
         */
        type: string,

        /**
         * URL to user's sign in page, passed in as a prop and typically generated via the auth provider
         */
        appSignInUrl: string,

        /**
         * signInToUrl = signInUrl + "&username=" + userData.memberName
         */
        appSignInToUrl: string,

        /**
         * URL to user's sign out page, passed in as a prop and typically generated via the auth provider
         */
        appSignOutUrl: string,

        /**
         * The sign out url but set the wreply = to the sign in url
         */
        appSwitchUrl: string,

        /**
         * For MSA this can be the same as the appSignInToUrl param
         */
        appSwitchToUrl: string
    },

    /**
     * MSA-specific properties for the Me Control
     */
    msa: {
        /**
         * The signOutUrl provided by the auth provider
         */
        signOutAndForgetUrl: string,

        /**
         * The meUrl provided by the auth provider
         */
        rememberedAccountsUrl: string
    },

    /**
     * Properties associated with the currently active MSA accout
     * Should be undefined if the user is signed out
     */
    currentAccount: {
        /**
         * The type of account (msa or aad)
         */
        type: string,

        /**
         * The current authentication state of the account (signedIn, notSignedIn, or signedInIDPOnly)
         */
        authenticatedState: string,

        /**
         * The member name associated with the user (often an email or phone number)
         */
        memberName: string,

        /**
         * The first name of the user
         */
        firstName: string,

        /**
         * The last name of the user
         */
        lastName: string,

        /**
         * The CID associated with the user
         */
        cid: string
    },

    /** UX configuration */
    primaryUXConfig: {
        /** The theme to use. */
        theme: {
            /** The theme type. */
            type: string
        }
    }
}

/**
 * Generates the options object that can be passed to the Me Control API to generate a Me Control
 * @param ssoData The ssoData containing the user information needed to populate the Me Control
 * @param useDarkTheme True if the control will be rendered on a dark background
 * @param locationHref the current window.location.href passed from requestContext
 */
export function generateMeControlOptions(ssoData: SSOSignInProperties, useDarkTheme: boolean, locationHref: string): MeControlComponentOptions {
    const signInUrl: string = formatMeControlUrl(baseSignInUrl, locationHref);
    const signOutUrl: string = formatMeControlUrl(baseSignOutUrl, locationHref);
    const meUrl: string = formatMeControlUrl(baseMeUrl, locationHref);

    // Build configuration object
    const meControlOptions: MeControlComponentOptions = {
        apiGeneration: "GEN2",
        containerId: meControlComponentContainerId,
        authProviderConfig: {
            type: "webMsaOnly",
            appSignInUrl: signInUrl,
            appSignInToUrl: signInUrl + "&username=" + ssoData.memberName,
            appSignOutUrl: signOutUrl,
            appSwitchUrl: signOutUrl + "&wreply=" + signInUrl,
            appSwitchToUrl: signInUrl + "&username=" + ssoData.memberName
        },
        msa: {
            signOutAndForgetUrl: signOutUrl,
            rememberedAccountsUrl: meUrl
        },
        currentAccount: undefined,
        primaryUXConfig: undefined
    };

    // currentAccount should be undefined if the user is signed out
    if (ssoData.ssoStatus === "sso_completed") {
        meControlOptions.currentAccount = {
            type: "msa",
            authenticatedState: "signedIn",
            memberName: ssoData.memberName,
            firstName: ssoData.firstName,
            lastName: ssoData.lastName,
            cid: ssoData.cid
        };
    }

    if (useDarkTheme) {
        meControlOptions.primaryUXConfig = {
            theme: {
                type: "dark"
            }
        };
    }

    return meControlOptions;
}

/**
 * Generates a URL for the Me Control by adding the market and return URL to a given base URL
 * @param baseUrl the baseUrl to format for the Me Control
 * @param locationHref the current window.location.href passed from requestContext
 * @returns The generated URL for the Me Control
 */
function formatMeControlUrl(baseUrl: string, locationHref: string): string {
    const market: string = appEnvironment.CurrentMarket;
    const returnUrl: string = encodeURIComponent(encodeURIComponent(locationHref));
    return String.Format(baseUrl, market, returnUrl);
}